import { IOption, IOptions } from '../../../common/common.types'
import { CadRecordDataPoint } from '../../cadImport/store/cadImport.types'
import {
  IDispositionOption,
  IEmsRegTailOption,
  IEmsVehicleOptions,
  IUnitOption,
} from '../../options/store/options.types'
import { ITimesState } from '../../times/store/times.types'
import { AIR_UNIT_TYPES } from '../store/tripInformation.constants'

export const getResponseMode = (
  emergency: number,
  responseModes: IOptions,
  unitLights: number | null
): IOption | null => {
  if ((unitLights && unitLights < 1) || unitLights == null) return null // Proceed only if lights > 0

  const codeMap: Record<number, string> = {
    1: '2223001', // Emergent
    0: '2223005', // Non-Emergent
  }

  const code = codeMap[emergency]
  if (!code) return null

  const option = responseModes.find((opt: IOption) => opt?.nemsis3 === Number(code)) || null
  return option
}

export const optionwWithStateIds = (
  selectedOption: IOption | null,
  options: IEmsVehicleOptions
): IEmsRegTailOption | IUnitOption | null => {
  if (!selectedOption) {
    return null
  }

  const stateIds = options.filter((option) => option?.id === selectedOption?.id)
  return { ...selectedOption, stateIds }
}

export const hideDisableDateTimesBasedOnDisposition = (
  disposition: IDispositionOption | null,
  times: ITimesState,
  importedCadData?: CadRecordDataPoint[]
): ITimesState => {
  const updates: Record<string, string | boolean | null> = {}
  const {
    at_ref_fields = null,
    at_rec_fields = null,
    override_destination_times = null,
    nemsis3 = 0,
  } = disposition || {}

  const makeKey = (prefix: string, base: string) => `${prefix}${base[0].toUpperCase()}${base.slice(1)}ForDisposition`

  const dateFields = [
    {
      key: 'arriveReceiving',
      disable: at_rec_fields === 0 || (at_ref_fields === 0 && at_rec_fields === 0),
      show: true,
    },
    {
      key: 'transferCareDestination',
      disable: at_rec_fields === 0 || (at_ref_fields === 0 && at_rec_fields === 0),
      show: nemsis3 !== 4212031 || (override_destination_times === 1 && nemsis3 === 4212031) || !disposition,
    },
    {
      key: 'transferCareEMS',
      disable: at_rec_fields === 0 || (at_ref_fields === 0 && at_rec_fields === 0),
      show: nemsis3 === 4212031,
    },
    {
      key: 'firstResponder',
      disable: at_ref_fields === 0 || (at_ref_fields === 0 && at_rec_fields === 0),
      show: true,
    },
    {
      key: 'atPatient',
      disable: at_ref_fields === 0 || (at_ref_fields === 0 && at_rec_fields === 0),
      show: true,
    },
    {
      key: 'leavePatient',
      disable: at_ref_fields === 0 || (at_ref_fields === 0 && at_rec_fields === 0),
      show: true,
    },
    {
      key: 'descent',
      disable: at_ref_fields === 0 || at_rec_fields === 0,
      show: true,
      clearValues: at_ref_fields === 0 || at_rec_fields === 0,
    },
    {
      key: 'atLanding',
      disable: at_ref_fields === 0 || at_rec_fields === 0,
      show: true,
      clearValues: at_ref_fields === 0 || at_rec_fields === 0,
    },
    {
      key: 'cancelled',
      disable: false,
      show: [4212007, 4212009, 4212011].includes(nemsis3),
    },
  ]

  for (const { key, disable, show, clearValues } of dateFields) {
    const disableKey = makeKey('__disable', key)
    const hideKey = makeKey('__hide', key)

    const dateKey = `${key}Date`
    const timeKey = `${key}Time`

    const dateValue = times[dateKey as keyof ITimesState] as string
    const timeValue = times[timeKey as keyof ITimesState] as string

    const cadValue = importedCadData?.find(
      (d) => d.appStateLocation.startsWith(`times.${dateKey}`) || d.appStateLocation.startsWith(`times.${timeKey}`)
    )?.value

    const noCad = !cadValue?.length
    const emptyFields = (!dateValue?.length && !timeValue?.length) || !!clearValues
    const shouldHide = !show || (disable && noCad && emptyFields)
    updates[disableKey] = disable
    updates[hideKey] = shouldHide

    if (shouldHide) {
      updates[dateKey] = ''
      updates[timeKey] = ''
    }
  }

  return { ...times, ...updates } as ITimesState
}

export const isAirUnit = (unitType: number | null): boolean => unitType !== null && AIR_UNIT_TYPES.includes(unitType)

export const supportsMaxAlt = (unit: IUnitOption | null): boolean => unit?.maxAltCapable === 1
